import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, WeakPasswordError } from '../contexts/KStackAuthContext';
import ThemeToggle from '../components/ThemeToggle';
import './Login.css';

/**
 * Self-service signup page (Phase F4, 2026-05-25).
 *
 * Posts to Nexus's POST /auth/register. Nexus mints a 6-digit
 * verify_email OTP server-side and (per gateway.config.yaml's
 * mailer.provider='console' on kstack-dev) writes it to journalctl;
 * P3-EM01 swaps in real SMTP for UAT/prod.
 *
 * Outcomes:
 *   - 201 OK              → /verify-email  (state: { email })
 *   - 409 PENDING_VERIF.  → /verify-email  (state: { email, info: 'verification pending…' })
 *   - 409 EMAIL_EXISTS    → inline error w/ "log in instead?" CTA
 *   - 400 WEAK_PASSWORD   → render the policy reasons returned by the server
 *
 * Field choices: email + password are required by the server; given_name +
 * family_name are optional but help the dashboard show a friendly greeting,
 * so we ask for them inline rather than punting to a profile-completion
 * step later.
 */
const SignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [passwordReasons, setPasswordReasons] = useState<string[]>([]);

  const navigate = useNavigate();
  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    setErrorMsg(null);
    setPasswordReasons([]);

    if (!email || !password) {
      setErrorMsg('Email and password are required.');
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      const result = await register(
        email,
        password,
        givenName.trim() || undefined,
        familyName.trim() || undefined,
      );
      switch (result.kind) {
        case 'ok':
          navigate('/verify-email', {
            state: {
              email: result.email,
              info:
                'Account created — check your email for a 6-digit verification code.',
            },
          });
          return;
        case 'pending_verification':
          navigate('/verify-email', {
            state: {
              email: result.email,
              info:
                'This email is already pending verification — a new code has been sent.',
            },
          });
          return;
        case 'email_exists':
          setErrorMsg(
            'An account with that email already exists. Sign in instead?',
          );
          return;
      }
    } catch (err) {
      if (err instanceof WeakPasswordError) {
        setPasswordReasons(err.reasons);
        setErrorMsg('Password does not meet policy:');
      } else {
        setErrorMsg(
          err instanceof Error ? err.message : 'Failed to create account.',
        );
      }
    } finally {
      setTimeout(() => setIsLoading(false), 100);
    }
  };

  return (
    <div className="login-container">
      <ThemeToggle placement="corner" />
      <div className="login-box">
        <div className="login-header">
          <h1>Create your account</h1>
        </div>
        <p>Set up a BolusNotes account to track and analyze your diabetes data.</p>

        {errorMsg && (
          <div className="error-modal" onClick={() => setErrorMsg(null)}>
            <div className="error-content" onClick={(e) => e.stopPropagation()}>
              <div className="error-message">❌ {errorMsg}</div>
              {passwordReasons.length > 0 && (
                <ul style={{ textAlign: 'left', margin: '12px 0' }}>
                  {passwordReasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              )}
              <button className="error-ok-button" onClick={() => setErrorMsg(null)}>
                OK
              </button>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              name="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              placeholder="you@example.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="form-group" style={{ display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label htmlFor="givenName">First name</label>
              <input
                type="text"
                id="givenName"
                value={givenName}
                onChange={(e) => setGivenName(e.target.value)}
                disabled={isLoading}
                placeholder="Optional"
                autoComplete="given-name"
              />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="familyName">Last name</label>
              <input
                type="text"
                id="familyName"
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                disabled={isLoading}
                placeholder="Optional"
                autoComplete="family-name"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                placeholder="At least 8 characters"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                disabled={isLoading}
              >
                {showPassword ? '👁️' : '🔒'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm password</label>
            <input
              type={showPassword ? 'text' : 'password'}
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              placeholder="Re-type your password"
              autoComplete="new-password"
              required
            />
          </div>

          <button type="submit" disabled={isLoading} className="btn-primary">
            {isLoading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <div className="divider">
          <span>already have an account?</span>
        </div>

        <Link to="/login" className="btn-secondary-link">
          Sign in instead
        </Link>
      </div>
    </div>
  );
};

export default SignUp;
